import React from "react";
import { FormattedMessage } from "react-intl";
import Flex from "./box/Flex";
import SvgBox from "./box/SvgBox";

interface IProjectStatusProps {
  status: string | null;
}

const ProjectStatus: React.FC<IProjectStatusProps> = ({ status }) => {
  if (status === "ideation") {
    return (
      <StatusBox color="teal.600">
        <FormattedProjectStatus status={status} />
        <IdeationIcon />
      </StatusBox>
    );
  }

  if (status === "planning") {
    return (
      <StatusBox color="blue.600">
        <FormattedProjectStatus status={status} />
        <PlanningIcon />
      </StatusBox>
    );
  }

  if (status === "inProgress") {
    return (
      <StatusBox color="blue.700">
        <FormattedProjectStatus status={status} />
        <InProgressIcon />
      </StatusBox>
    );
  }

  if (status === "complete") {
    return (
      <StatusBox color="blue.800">
        <FormattedProjectStatus status={status} />
        <CompleteIcon />
      </StatusBox>
    );
  }

  return null;
};

interface IStatusBox {
  color: string;
}

const StatusBox: React.FC<IStatusBox> = ({ color, children }) => {
  return (
    <Flex
      border="solid 1px"
      display="inline-flex"
      borderColor={color}
      color={color}
      borderRadius="xl"
      px={2}
      py={1}
      alignItems="center"
    >
      {children}
    </Flex>
  );
};

const IdeationIcon: React.FC = () => (
  <SvgBox viewBox="0 0 14 22" width="14px" height="22px" ml={2}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.66975 0.993042C2.98596 0.993042 0 3.979 0 7.66279C0 8.60124 0.193954 9.49436 0.543871 10.3042C1.01443 11.3939 2.18815 13.0562 2.93664 15.377C3.0786 15.9695 3.35054 16.3881 3.35054 17.7037C3.35054 18.3936 3.98639 19.1027 4.73421 19.6226C4.7802 19.6553 4.80619 19.7173 4.82619 19.7686C5.02681 20.2751 5.40538 21.007 5.64599 21.007C6.29584 21.007 6.93502 20.9996 7.6002 20.9996C7.8528 20.9996 8.28803 20.2758 8.51131 19.7699C8.53064 19.7259 8.54997 19.6786 8.59129 19.6426C9.34578 19.1314 9.98896 18.4256 9.98896 17.7037C9.98896 16.3267 10.2609 15.9702 10.4029 15.377C11.1507 13.0562 12.3251 11.3939 12.7956 10.3042C13.1455 9.49436 13.3388 8.60124 13.3388 7.66279C13.3395 3.979 10.3535 0.993042 6.66975 0.993042ZM11.5886 9.7343C10.8168 11.4999 9.72303 13.4248 9.06452 15.2823C8.75126 16.1988 8.03276 16.3221 7.66018 16.3367C7.43623 16.3367 5.92059 16.3367 5.70598 16.3367C4.66822 16.2927 4.27432 15.2856 4.27432 15.2856C3.61581 13.4281 2.52673 11.4999 1.75492 9.73363C1.48631 9.09645 1.33835 8.39728 1.33835 7.66213C1.33835 4.77814 3.62714 2.4287 6.48713 2.33006C6.6151 2.32539 6.72907 2.32539 6.85704 2.33006C9.71703 2.4287 12.0058 4.77814 12.0058 7.66213C12.0058 8.39728 11.8572 9.09712 11.5886 9.7343ZM7.37692 3.72373C7.14764 3.6824 6.91103 3.66108 6.66975 3.66108C4.45961 3.66108 2.66803 5.45265 2.66803 7.66279C2.66803 7.94606 2.69736 8.22199 2.75335 8.4886C2.79934 8.58991 2.81866 8.60524 2.91664 8.66122C2.96596 8.68922 3.27189 8.7692 3.45585 8.79719C3.63914 8.82518 3.70112 8.80119 3.68912 8.68455C3.68912 6.47441 5.4807 4.68283 7.69084 4.68283C7.80415 4.6895 7.96344 4.27093 7.97144 4.02832C7.97677 3.87436 7.7055 3.79238 7.37692 3.72373Z"
      fill="currentColor"
    />
  </SvgBox>
);

const PlanningIcon: React.FC = () => (
  <SvgBox viewBox="0 0 19 20" width="19px" height="20px" ml={2} mb="1px">
    <path
      d="M13.0674 9.83506C13.4262 10.6826 14.5097 11.0417 15.484 10.6292L16.3529 10.2613L18.2014 14.6276L13.4619 16.6342L13.1031 15.7866C12.7007 14.8363 11.6636 14.3668 10.7947 14.7346C9.92579 15.1025 9.54108 16.174 9.94341 17.1243L10.3022 17.9719L7.14258 19.3096C6.5633 19.5549 5.89362 19.2932 5.6544 18.7282L1.08738 7.94084L5.30028 6.15723L4.94144 5.30966C4.53911 4.35935 4.92382 3.28784 5.79273 2.91997C6.66164 2.5521 7.69878 3.02164 8.10111 3.97195L8.45995 4.81953L13.1995 2.81297L15.048 7.17926L14.1791 7.54713C13.2049 7.95959 12.7085 8.98748 13.0674 9.83506Z"
      fill="currentColor"
    />
  </SvgBox>
);

const InProgressIcon: React.FC = () => (
  <SvgBox viewBox="0 0 28 21" width="28px" height="21px" ml={2}>
    <path d="M16.459 6.68224L16.527 4.87366L17.8908 4.59998L18.7318 6.22601L16.459 6.68224Z" fill="currentColor" />
    <path
      d="M20.1075 17.6164C23.2456 16.9864 25.2489 14.073 24.5819 11.1092C23.9148 8.14534 20.8301 6.25339 17.6919 6.88337C14.5537 7.51336 12.5505 10.4267 13.2175 13.3906C13.8845 16.3544 16.9693 18.2464 20.1075 17.6164Z"
      fill="currentColor"
    />
    <path d="M19.0678 18.2739L19.9088 19.9L21.2726 19.6263L21.3406 17.8177L19.0678 18.2739Z" fill="currentColor" />
    <path d="M25.2781 12.0913L26.9998 11.2969L26.7099 10.0089L24.795 9.9446L25.2781 12.0913Z" fill="currentColor" />
    <path d="M11.0897 14.4908L13.0046 14.5552L12.5215 12.4085L10.7998 13.2029L11.0897 14.4908Z" fill="currentColor" />
    <path d="M13.0707 9.90347L11.7648 8.57917L12.5241 7.47483L14.3363 8.06294L13.0707 9.90347Z" fill="currentColor" />
    <path d="M14.3585 18.7162L14.9812 17.0047L16.9299 18.1999L15.5277 19.4333L14.3585 18.7162Z" fill="currentColor" />
    <path d="M24.8852 14.5677L26.1911 15.892L25.4318 16.9964L23.6196 16.4083L24.8852 14.5677Z" fill="currentColor" />
    <path d="M23.9254 6.08083L23.3027 7.79232L21.3539 6.59706L22.7561 5.36373L23.9254 6.08083Z" fill="currentColor" />
    <path
      d="M16.459 6.68224L16.527 4.87366L17.8908 4.59998L18.7318 6.22601L16.459 6.68224Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M20.1075 17.6164C23.2456 16.9864 25.2489 14.073 24.5819 11.1092C23.9148 8.14534 20.8301 6.25339 17.6919 6.88337C14.5537 7.51336 12.5505 10.4267 13.2175 13.3906C13.8845 16.3544 16.9693 18.2464 20.1075 17.6164Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M19.0678 18.2739L19.9088 19.9L21.2726 19.6263L21.3406 17.8177L19.0678 18.2739Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M25.2781 12.0913L26.9998 11.2969L26.7099 10.0089L24.795 9.9446L25.2781 12.0913Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M11.0897 14.4908L13.0046 14.5552L12.5215 12.4085L10.7998 13.2029L11.0897 14.4908Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M13.0707 9.90347L11.7648 8.57917L12.5241 7.47483L14.3363 8.06294L13.0707 9.90347Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M14.3585 18.7162L14.9812 17.0047L16.9299 18.1999L15.5277 19.4333L14.3585 18.7162Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M24.8852 14.5677L26.1911 15.892L25.4318 16.9964L23.6196 16.4083L24.8852 14.5677Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M23.9254 6.08083L23.3027 7.79232L21.3539 6.59706L22.7561 5.36373L23.9254 6.08083Z"
      stroke="#086BE5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.081 5.16723C10.8852 4.46314 10.5218 3.83059 10.0385 3.3046L10.555 2.28215L9.77503 1.66701L8.80348 2.33489C8.17696 1.98737 7.46716 1.76913 6.72018 1.71403L6.33972 0.702199L5.33516 0.782134L5.16912 1.83749C4.56043 1.98233 4.00139 2.23663 3.51488 2.5762L2.3436 2.03663L1.70294 2.76491L2.40486 3.66769C1.89123 4.38215 1.59022 5.24264 1.58103 6.16538L0.512981 6.51138L0.611519 7.46131L1.81982 7.64625C2.07326 8.38762 2.51553 9.04077 3.08666 9.56164L2.59252 10.5397L3.37253 11.1548L4.42367 10.4322C5.14477 10.7517 5.954 10.9081 6.79105 10.857L7.0961 11.6682L8.10066 11.5883L8.26531 10.5417C8.88451 10.3064 9.43624 9.95244 9.89148 9.51062L10.7782 9.91908L11.4188 9.19083L10.7602 8.34376C11.0285 7.82441 11.1942 7.24971 11.2342 6.64426L12.3215 6.29202L12.223 5.34209L11.081 5.16723ZM6.06476 2.93406C4.07614 3.09237 2.65863 4.73695 2.84848 6.56706C3.03832 8.39717 4.77102 9.79098 6.75964 9.63268C8.74826 9.47437 10.1658 7.82978 9.97592 5.99967C9.78608 4.16956 8.05338 2.77575 6.06476 2.93406Z"
      fill="currentColor"
    />
  </SvgBox>
);

const CompleteIcon: React.FC = () => (
  <SvgBox viewBox="0 0 16 20" width="16px" height="20px" ml={2}>
    <path
      d="M1 12.625C1 12.625 1.875 11.75 4.5 11.75C7.125 11.75 8.875 13.5 11.5 13.5C14.125 13.5 15 12.625 15 12.625V2.125C15 2.125 14.125 3 11.5 3C8.875 3 7.125 1.25 4.5 1.25C1.875 1.25 1 2.125 1 2.125V12.625Z"
      fill="currentColor"
    />
    <path
      d="M1 12.625C1 12.625 1.875 11.75 4.5 11.75C7.125 11.75 8.875 13.5 11.5 13.5C14.125 13.5 15 12.625 15 12.625V2.125C15 2.125 14.125 3 11.5 3C8.875 3 7.125 1.25 4.5 1.25C1.875 1.25 1 2.125 1 2.125V12.625Z"
      fill="currentColor"
    />
    <path
      d="M1 12.625C1 12.625 1.875 11.75 4.5 11.75C7.125 11.75 8.875 13.5 11.5 13.5C14.125 13.5 15 12.625 15 12.625V2.125C15 2.125 14.125 3 11.5 3C8.875 3 7.125 1.25 4.5 1.25C1.875 1.25 1 2.125 1 2.125V12.625Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path
      d="M1 12.625C1 12.625 1.875 11.75 4.5 11.75C7.125 11.75 8.875 13.5 11.5 13.5C14.125 13.5 15 12.625 15 12.625V2.125C15 2.125 14.125 3 11.5 3C8.875 3 7.125 1.25 4.5 1.25C1.875 1.25 1 2.125 1 2.125V12.625Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
    <path d="M1 18.75V12.625" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </SvgBox>
);

interface IFormattedProjectStatusProps {
  status: string | null;
}

export function FormattedProjectStatus({ status }: IFormattedProjectStatusProps) {
  if (status === null) return null;

  switch (status.toLowerCase()) {
    case "ideation":
      return <FormattedMessage defaultMessage="Ideation" id="v3wrFv" />;
    case "planning":
      return <FormattedMessage defaultMessage="Planning" id="99OdS3" />;
    case "inprogress":
      return <FormattedMessage defaultMessage="In Progress" id="B487HA" />;
    case "completed":
    case "complete":
      return <FormattedMessage defaultMessage="Completed" id="95stPq" />;
    case "product":
      return <FormattedMessage defaultMessage="Product" id="x/ZVlU" />;
    default:
      return <span>{status}</span>;
  }
}

export default ProjectStatus;
